import { currencyFormatter, dateFormatter, formatDateShort } from '@lendica/utils';
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import { format, addDays, addWeeks, addMonths, parseISO } from 'date-fns';

export const PaymentDetails = ({ open = false, offer, bill, darkMode = true }) => {
    const dueDate = bill.due_date ? parseISO(bill.due_date) : new Date();
    const numPayments = offer.number_of_payments;

    // Calculate payment dates correctly based on frequency
    const firstPaymentDate = addDays(dueDate, offer.first_payment_delay);
    let lastPaymentDate;
    if (offer.payment_frequency === 'W') {
        // Weekly payments
        lastPaymentDate = addWeeks(firstPaymentDate, numPayments - 1);
    } else if (offer.payment_frequency === 'M') {
        // Monthly payments
        lastPaymentDate = addMonths(firstPaymentDate, numPayments - 1);
    } else {
        // Daily payments (or default)
        lastPaymentDate = addDays(firstPaymentDate, numPayments - 1);
    }

    let totalFees = offer.interest + offer.origination_fee;
    let deposit = offer.total_price - offer.advance_amount;
    let paymentOnInvoiceDueDate = deposit + offer.origination_fee;
    let totalPaymentAmount = offer.total_payments + deposit + offer.origination_fee;

    let timeline = [
        format(firstPaymentDate, 'MMMM d, yyyy'),
        format(lastPaymentDate, 'MMMM d, yyyy'),
    ];

    let paymentFrequency;

    if (offer.payment_frequency === 'W') {
        paymentFrequency = 'week';
    } else if (offer.payment_frequency === 'M') {
        paymentFrequency = 'month';
    } else {
        paymentFrequency = 'day';
    }

    return (
        !!offer &&
        !!bill && (
            <Accordion
                sx={{
                    'boxShadow': 0,
                    'borderRadius': 2,
                    'boxSizing': 'border-box',
                    'border': 'none',
                    'background': darkMode ? '#282761' : '#F4F4F6',
                    'width': '100%',
                    '&:before': {
                        display: 'none',
                    },
                    'padding': 1,
                }}
                disableGutters={true}
                defaultExpanded={open}
            >
                <AccordionSummary
                    expandIcon={
                        <ArrowDropDown color={darkMode ? 'text.disabled' : 'text.secondary'} />
                    }
                >
                    <Typography
                        fontSize={13}
                        fontWeight={500}
                        color={darkMode ? '#FCFCFD' : 'info.main'}
                    >
                        Payment Schedule
                    </Typography>
                </AccordionSummary>

                <AccordionDetails>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'flex-start',
                            mb: 2,
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <Box mb={1}>
                                <Typography
                                    color={darkMode ? 'text.disabled' : 'text.secondary'}
                                    variant="caption"
                                    fontSize={12}
                                    mb={1}
                                >
                                    Payment on invoice due date
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
                                    <Typography
                                        color={darkMode ? '#FCFCFD' : 'text.primary'}
                                        variant="h4"
                                        fontWeight={600}
                                        fontSize={16}
                                    >
                                        {currencyFormatter(paymentOnInvoiceDueDate)}
                                    </Typography>
                                    {bill.due_date && (
                                        <Typography
                                            color={darkMode ? '#FCFCFD' : 'text.primary'}
                                            variant="body1"
                                            fontWeight={400}
                                            fontSize={14}
                                            ml={1}
                                        >
                                            {`on ${dateFormatter(bill.due_date)}`}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            borderBottom: '1px solid rgba(74, 74, 104, 0.5)',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <Box mb={2}>
                                <Typography
                                    color={darkMode ? 'text.disabled' : 'text.secondary'}
                                    variant="caption"
                                    fontSize={12}
                                    mb={1}
                                >
                                    Installments
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography
                                        color={darkMode ? '#FCFCFD' : 'text.primary'}
                                        fontWeight={600}
                                        fontSize={16}
                                    >
                                        {currencyFormatter(offer.periodic_payment)}
                                    </Typography>
                                    {offer.number_of_payments === 1 ? (
                                        <Typography
                                            color={darkMode ? '#FCFCFD' : 'text.primary'}
                                            variant="body1"
                                            fontWeight={400}
                                            fontSize={14}
                                            ml={1}
                                        >
                                            {`on ${format(firstPaymentDate, 'MMMM d, yyyy')}`}
                                        </Typography>
                                    ) : (
                                        <Typography
                                            color={darkMode ? '#FCFCFD' : 'text.primary'}
                                            variant="body1"
                                            fontWeight={400}
                                            fontSize={14}
                                            ml={1}
                                        >
                                            {`every ${paymentFrequency}, for ${offer.number_of_payments} ${paymentFrequency}s`}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                        {offer.number_of_payments !== 1 && bill.due_date && (
                            <Box sx={{ alignSelf: 'flex-end' }}>
                                <PaymentTimeline
                                    offer={offer}
                                    timeline={timeline}
                                    darkMode={darkMode}
                                />
                            </Box>
                        )}
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            mt: 2,
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <Box>
                                <Typography
                                    color={darkMode ? 'text.disabled' : 'text.secondary'}
                                    variant="caption"
                                    fontSize={12}
                                    mb={0.5}
                                >
                                    Total fees
                                </Typography>
                                <Typography
                                    color={darkMode ? '#FCFCFD' : 'text.primary'}
                                    variant="h4"
                                    fontSize={16}
                                >{`${currencyFormatter(totalFees)}*`}</Typography>
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <Box>
                                <Typography
                                    color={darkMode ? 'text.disabled' : 'text.secondary'}
                                    variant="caption"
                                    fontSize={12}
                                    mb={0.5}
                                >
                                    Total payments
                                </Typography>
                                <Typography
                                    color={darkMode ? '#FCFCFD' : 'text.primary'}
                                    variant="h4"
                                    fontSize={16}
                                    textAlign="right"
                                >
                                    {currencyFormatter(totalPaymentAmount)}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    <Typography
                        mt={2}
                        color={darkMode ? 'text.disabled' : 'text.secondary'}
                        variant="caption"
                        fontWeight={400}
                        fontSize={12}
                        textAlign="left"
                    >{`*${currencyFormatter(
                        offer.origination_fee
                    )} processing fee + ${currencyFormatter(offer.interest)} interest`}</Typography>
                </AccordionDetails>
            </Accordion>
        )
    );
};

const PaymentTimeline = ({ offer, timeline, darkMode, totalPaymentAmount }) => {
    return (
        <Timeline sx={{ margin: 0, p: 0 }}>
            <TimelineItem key={'first-payment'}>
                <TimelineSeparator>
                    <TimelineDot sx={{ fill: darkMode ? 'text.disabled' : 'text.secondary' }} />
                    <TimelineConnector
                        sx={{ fill: darkMode ? 'text.disabled' : 'text.secondary' }}
                    />
                </TimelineSeparator>
                <TimelineContent>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            width: 250,
                        }}
                    >
                        <Typography
                            variant="body1"
                            color={darkMode ? 'text.disabled' : 'text.secondary'}
                            fontSize={16}
                            fontWeight={400}
                        >
                            {timeline[0]}
                        </Typography>
                        <Typography
                            color={darkMode ? 'text.disabled' : 'text.secondary'}
                            variant="caption"
                            fontSize={16}
                            fontWeight
                        >
                            {currencyFormatter(offer.periodic_payment)}
                        </Typography>
                    </Box>
                    <Typography
                        color={darkMode ? 'text.disabled' : 'text.secondary'}
                        variant="body1"
                        fontSize={12}
                    >
                        First installment
                    </Typography>
                </TimelineContent>
            </TimelineItem>

            {offer.number_of_payments > 2 && (
                <TimelineItem key={'placeholder'}>
                    <TimelineSeparator>
                        <TimelineDot sx={{ fill: darkMode ? 'text.disabled' : 'text.secondary' }} />
                        <TimelineConnector
                            sx={{ fill: darkMode ? 'text.disabled' : 'text.secondary' }}
                        />
                    </TimelineSeparator>
                    <TimelineContent>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                                width: 250,
                            }}
                        >
                            <Typography
                                color={darkMode ? 'text.disabled' : 'text.secondary'}
                                variant="caption"
                                fontSize={16}
                            >
                                ...
                            </Typography>
                        </Box>
                    </TimelineContent>
                </TimelineItem>
            )}

            <TimelineItem key={'last-payment'}>
                <TimelineSeparator>
                    <TimelineDot sx={{ fill: darkMode ? 'text.disabled' : 'text.secondary' }} />
                </TimelineSeparator>
                <TimelineContent>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            width: 250,
                        }}
                    >
                        <Typography
                            variant="body1"
                            color={darkMode ? 'text.disabled' : 'text.secondary'}
                            fontSize={16}
                            fontWeight={400}
                        >
                            {timeline[1]}
                        </Typography>
                        <Typography
                            color={darkMode ? 'text.disabled' : 'text.secondary'}
                            variant="caption"
                            fontSize={16}
                        >
                            {currencyFormatter(offer.periodic_payment)}
                        </Typography>
                    </Box>
                    <Typography
                        color={darkMode ? 'text.disabled' : 'text.secondary'}
                        variant="body1"
                        fontSize={12}
                    >
                        Last installment
                    </Typography>
                </TimelineContent>
            </TimelineItem>
        </Timeline>
    );
};
